import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

interface PlatformStats {
  total_users: number
  free_users: number
  pro_users: number
  enterprise_users: number
  suspended_users: number
  total_credits_used: number
  total_activities: number
  total_content: number
  activities_today: number
  new_users_this_month: number
}

interface User {
  id: string
  email: string
  full_name: string
  role: string
  plan_type: string
  credits_used: number
  credits_limit: number
  is_suspended: boolean
  last_reset_date: string
  created_at: string
  last_activity: string | null
}

export function useSuperAdmin() {
  const { user, profile } = useAuth()
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Check if current user is superadmin
  useEffect(() => {
    const checkSuperAdminStatus = async () => {
      if (!user) {
        setIsSuperAdmin(false)
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase.rpc('is_superadmin', {
          user_id: user.id
        })

        if (error) throw error
        setIsSuperAdmin(!!data)
      } catch (err) {
        console.error('Error checking superadmin status:', err)
        setIsSuperAdmin(false)
      } finally {
        setLoading(false)
      }
    }

    checkSuperAdminStatus()
  }, [user])

  // Get platform statistics
  const getPlatformStats = useCallback(async (): Promise<PlatformStats | null> => {
    if (!isSuperAdmin) {
      setError(new Error('Unauthorized: SuperAdmin access required'))
      return null
    }

    try {
      const { data, error } = await supabase.rpc('get_platform_stats')

      if (error) throw error
      return data as PlatformStats
    } catch (err) {
      console.error('Error fetching platform stats:', err)
      setError(err as Error)
      return null
    }
  }, [isSuperAdmin])

  // Get all users (paginated)
  const getAllUsers = useCallback(async (
    limit: number = 50,
    offset: number = 0
  ): Promise<User[] | null> => {
    if (!isSuperAdmin) {
      setError(new Error('Unauthorized: SuperAdmin access required'))
      return null
    }

    try {
      const { data, error } = await supabase.rpc('get_all_users', {
        limit_count: limit,
        offset_count: offset
      })

      if (error) throw error
      return data as User[]
    } catch (err) {
      console.error('Error fetching users:', err)
      setError(err as Error)
      return null
    }
  }, [isSuperAdmin])

  // Update user plan
  const updateUserPlan = useCallback(async (
    userId: string,
    newPlan: 'free' | 'pro' | 'enterprise',
    newLimit: number,
    notes?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!isSuperAdmin) {
      return { success: false, error: 'Unauthorized: SuperAdmin access required' }
    }

    try {
      const { error } = await supabase.rpc('admin_update_user_plan', {
        target_user_id: userId,
        new_plan_type: newPlan,
        new_credits_limit: newLimit,
        admin_notes: notes
      })

      if (error) throw error
      return { success: true }
    } catch (err) {
      console.error('Error updating user plan:', err)
      return { success: false, error: (err as Error).message }
    }
  }, [isSuperAdmin])

  // Reset user credits
  const resetUserCredits = useCallback(async (
    userId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!isSuperAdmin) {
      return { success: false, error: 'Unauthorized: SuperAdmin access required' }
    }

    try {
      const { error } = await supabase.rpc('admin_reset_user_credits', {
        target_user_id: userId,
        reason: reason || 'Admin manual reset'
      })

      if (error) throw error
      return { success: true }
    } catch (err) {
      console.error('Error resetting user credits:', err)
      return { success: false, error: (err as Error).message }
    }
  }, [isSuperAdmin])

  // Suspend or unsuspend user
  const toggleSuspension = useCallback(async (
    userId: string,
    suspend: boolean,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!isSuperAdmin) {
      return { success: false, error: 'Unauthorized: SuperAdmin access required' }
    }

    try {
      const { error } = await supabase.rpc('admin_toggle_suspension', {
        target_user_id: userId,
        suspend,
        reason
      })

      if (error) throw error
      return { success: true }
    } catch (err) {
      console.error('Error toggling suspension:', err)
      return { success: false, error: (err as Error).message }
    }
  }, [isSuperAdmin])

  // Grant bonus credits
  const grantBonusCredits = useCallback(async (
    userId: string,
    bonusAmount: number,
    reason?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!isSuperAdmin) {
      return { success: false, error: 'Unauthorized: SuperAdmin access required' }
    }

    try {
      const { error } = await supabase.rpc('admin_grant_bonus_credits', {
        target_user_id: userId,
        bonus_amount: bonusAmount,
        reason: reason || 'Admin bonus'
      })

      if (error) throw error
      return { success: true }
    } catch (err) {
      console.error('Error granting bonus credits:', err)
      return { success: false, error: (err as Error).message }
    }
  }, [isSuperAdmin])

  // Get user details
  const getUserDetails = useCallback(async (userId: string) => {
    if (!isSuperAdmin) {
      setError(new Error('Unauthorized: SuperAdmin access required'))
      return null
    }

    try {
      const { data, error } = await supabase.rpc('admin_get_user_details', {
        target_user_id: userId
      })

      if (error) throw error
      return data
    } catch (err) {
      console.error('Error fetching user details:', err)
      setError(err as Error)
      return null
    }
  }, [isSuperAdmin])

  // Delete user account
  const deleteUser = useCallback(async (
    userId: string,
    reason?: string
  ): Promise<{ success: boolean; error?: string; data?: any }> => {
    if (!isSuperAdmin) {
      return { success: false, error: 'Unauthorized: SuperAdmin access required' }
    }

    try {
      const { data, error } = await supabase.rpc('admin_delete_user', {
        target_user_id: userId,
        reason: reason || 'Admin deletion'
      })

      if (error) throw error

      // Check if the function returned an error
      if (data && !data.success) {
        return { success: false, error: data.error }
      }

      return { success: true, data }
    } catch (err) {
      console.error('Error deleting user:', err)
      return { success: false, error: (err as Error).message }
    }
  }, [isSuperAdmin])

  return {
    isSuperAdmin,
    loading,
    error,
    getPlatformStats,
    getAllUsers,
    updateUserPlan,
    resetUserCredits,
    toggleSuspension,
    grantBonusCredits,
    getUserDetails,
    deleteUser
  }
}
